'use client';

import classNames from 'classnames';
import dynamic from 'next/dynamic';
import { ComponentProps, ReactNode, useCallback, useEffect, useId, useRef, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import CarouselDisplayTrigger from '@alltrails/amplitude/enums/CarouselDisplayTrigger';
import NullState from '@alltrails/amplitude/enums/NullState';
import NullStateAction from '@alltrails/amplitude/enums/NullStateAction';
import ReviewShowMoreSection from '@alltrails/amplitude/enums/ReviewShowMoreSection';
import logActivityCardClicked from '@alltrails/amplitude/events/logActivityCardClicked';
import logCommunityProfileClicked from '@alltrails/amplitude/events/logCommunityProfileClicked';
import logNullStateCTAClicked from '@alltrails/amplitude/events/logNullStateCTAClicked';
import logNullStateViewed from '@alltrails/amplitude/events/logNullStateViewed';
import logPhotoCarouselSwiped from '@alltrails/amplitude/events/logPhotoCarouselSwiped';
import logReviewFilterButtonClicked from '@alltrails/amplitude/events/logReviewFilterButtonClicked';
import logReviewFiltersShowMoreClicked from '@alltrails/amplitude/events/logReviewFiltersShowMoreClicked';
import logReviewSearchResultsReturned from '@alltrails/amplitude/events/logReviewSearchResultsReturned';
import logReviewsSearchBoxClicked from '@alltrails/amplitude/events/logReviewsSearchBoxClicked';
import logTrailDetailsActivityTabClicked from '@alltrails/amplitude/events/logTrailDetailsActivityTabClicked';
import logTrailDetailsReviewsPhotoClicked from '@alltrails/amplitude/events/logTrailDetailsReviewsPhotoClicked';
import logTrailDetailsReviewsPhotoEndReached from '@alltrails/amplitude/events/logTrailDetailsReviewsPhotoEndReached';
import logTrailDetailsReviewTabClicked from '@alltrails/amplitude/events/logTrailDetailsReviewTabClicked';
import logTrailDetailsSeeAllClicked from '@alltrails/amplitude/events/logTrailDetailsSeeAllClicked';
import logTrailDetailsWriteReviewClicked from '@alltrails/amplitude/events/logTrailDetailsWriteReviewClicked';
import useUser from '@alltrails/context/hooks/useUser';
import useMediaQuery from '@alltrails/core/hooks/useMediaQuery';
import { get } from '@alltrails/core/utils/get';
import Button from '@alltrails/denali/components/Button';
import Link from '@alltrails/denali/components/Link';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import { BREAKPOINT_MD_SIZE } from '@alltrails/denali/tokens';
import type { LightboxPhoto, LightboxUser } from '@alltrails/denali/types';
import ReviewsGraph from '@alltrails/reviews/components/ReviewsGraph/ReviewsGraph';
import SearchFilterReviews from '@alltrails/reviews/components/SearchFilterReviews/SearchFilterReviews';
import useRatingsBreakdown from '@alltrails/reviews/hooks/useRatingsBreakdown';
import type { ReviewsApiResponse } from '@alltrails/reviews/slices/reviewsApi';
import { type TrailPhotoLocationInfo, useTrailAggregatePhotoViewed } from '@alltrails/trails';
import type { Review } from '@alltrails/types/Review';
import type { ReviewPhoto } from '@alltrails/types/ReviewPhoto';
import type TrailDetails from '@alltrails/types/TrailDetails';
import type { TrailId } from '@alltrails/types/TrailId';
import type { User } from '@alltrails/types/User';
import { REVIEWS_LOAD_MORE_COUNT } from '@/utils/constants';
import LoadingActivitesList from '../ActivitiesList/LoadingActivitiesList';
import AverageRating from '../AverageRating/AverageRating';
import layoutStyles from '../layouts/layoutStyles.module.scss';
import UgcActions from '../UgcActions/UgcActions';
import styles from './Reviews.module.scss';
import ReviewsNullState from './ReviewsNullState';

const ReviewFormModal = dynamic(() => import('@alltrails/reviews/components/NewReviewFormModal/NewReviewFormModal'));
const SignUpModal = dynamic(() => import('@alltrails/user-auth/components/SignUpModal'), { ssr: false });
const ActivitiesList = dynamic(() => import('@/components/ActivitiesList'), { loading: () => <LoadingActivitesList /> });

type ReviewsProps = {
  initialReviews?: ReviewsApiResponse;
  initialReviewIdFromDeeplink?: number;
  perPage?: number;
  reviewSummaryCardForMobile?: ReactNode;
  trailDetails: TrailDetails;
  trailInfo: TrailPhotoLocationInfo & { id: TrailId };
  isBot?: boolean;
  helpfulReviews?: Review[];
  seoTestLimitReviewsToFive?: boolean;
  seoTestLimitReviewsToTen?: boolean;
  seoTestLimitReviewsToThreeLonger?: boolean;
  seoTestDisplay5KeywordReviews?: boolean;
  seoKeywordReviews?: Review[];
};

const Reviews = ({
  initialReviews,
  initialReviewIdFromDeeplink,
  perPage = REVIEWS_LOAD_MORE_COUNT,
  reviewSummaryCardForMobile,
  trailDetails,
  trailInfo,
  isBot,
  helpfulReviews,
  seoTestLimitReviewsToFive,
  seoTestLimitReviewsToTen,
  seoTestLimitReviewsToThreeLonger,
  seoTestDisplay5KeywordReviews,
  seoKeywordReviews
}: ReviewsProps) => {
  const intl = useIntl();
  const {
    avgRating,
    id,
    ratingsBreakdown: initialRatingsBreakdown,
    slug,
    trailCounts: { trackCount }
  } = trailDetails;
  const user = useUser();
  const isMediumDown = useMediaQuery(`(max-width: ${BREAKPOINT_MD_SIZE}px)`);
  const reviewsHeadingId = useId();
  const [displayActivities, setDisplayActivities] = useState(false);
  const [tryEditFromDeeplink, setTryEditFromDeeplink] = useState(initialReviewIdFromDeeplink !== undefined);
  const [isSignupModalOpen, toggleSignupModal] = useState(tryEditFromDeeplink && !user);
  const [isReviewFormOpen, toggleReviewForm] = useState(false);
  const [onPhotoView, onPhotoViewComplete] = useTrailAggregatePhotoViewed({ source: 'trail review', trailId: id });
  const [signUpTrigger, setSignUpTrigger] = useState<CarouselDisplayTrigger | undefined>();
  const [reviewFormProps, setReviewFormProps] = useState<Omit<ComponentProps<typeof ReviewFormModal>, 'closeReviewModal'> | undefined>(undefined);
  const [ratingsBreakdown, setRatingsBreakdown] = useState(initialRatingsBreakdown ?? {});
  const { totalReviewsCount } = useRatingsBreakdown(ratingsBreakdown);
  const openReviewFormAfterAuth = useRef(tryEditFromDeeplink && !user);
  const reviewFromDeeplink = useRef<Review | undefined>(undefined);
  const containerRef = useRef<HTMLDivElement>(null);
  const ugcRef = useRef<HTMLDivElement>(null);
  const carouselSwipedRef = useRef(new Set<Review['id']>());
  const isMobile = useIsMobileSizedScreen();

  const onActivityEntryClicked = () => {
    logTrailDetailsActivityTabClicked({ trail_id: id });

    if (!user) {
      toggleSignupModal(true);
      return;
    }
    setDisplayActivities(true);
    isMediumDown ? ugcRef.current?.scrollIntoView({ behavior: 'smooth' }) : containerRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const onActivityLinkClick = (review: Review) => {
    logActivityCardClicked({
      detailed_card_location: 'trail details',
      map_id: String(review.associatedRecording?.id),
      owner_id: String(review.user.id)
    });
  };

  const onFilterClick = () => logReviewFilterButtonClicked({ trail_id: id });

  const onFilterShowMoreClick = (section: ReviewShowMoreSection) => logReviewFiltersShowMoreClicked({ show_more_section: section, trail_id: id });

  const onLightboxPhotoViewed = (_: Review, photo: LightboxPhoto['photo']) => {
    if (photo) {
      onPhotoView(photo.id);
    }
  };

  const onLightboxUserClick = (user: LightboxUser) =>
    logCommunityProfileClicked({
      carousel_position: -1,
      profile_location: 'trail detail photo',
      profile_user_id: user.id
    });

  const onLoadMore = () => {
    logTrailDetailsSeeAllClicked({ trail_id: id, location: 'reviews', trail_page_type: 'trail detail page' });

    if (!user) {
      setSignUpTrigger('See More Reviews');
      toggleSignupModal(true);
      return false;
    }

    return true;
  };

  const onAddReview = useCallback(() => {
    logTrailDetailsWriteReviewClicked({ trail_id: id });

    if (!user) {
      setSignUpTrigger('See More Reviews');
      toggleSignupModal(true);
      return;
    }

    openReviewFormModal({});
    return;
  }, [id, user]);

  const onNullStateCtaClick = (action: NullStateAction, state: NullState) =>
    logNullStateCTAClicked({ null_action: action, null_location: 'trail details review search', null_state: state, trail_id: id });

  const onNullStateViewed = (state: NullState) =>
    logNullStateViewed({ null_location: 'trail details review search', null_state: state, trail_id: id });

  const onResultsReturned: ComponentProps<typeof SearchFilterReviews>['onResultsReturned'] = ({
    action,
    activityTypes,
    entityType,
    months,
    query,
    ratings
  }) => {
    logReviewSearchResultsReturned({
      action,
      activity_type_filters: activityTypes?.length ? activityTypes : undefined,
      query_term: query || undefined,
      rating_filters: ratings?.length ? ratings : undefined,
      show_filters: entityType === 'photosOnly' ? 'reviews with photos' : 'all reviews',
      time_of_year_filters: months?.length ? months : undefined,
      trail_id: id
    });
  };

  const onReviewAdd = ({ detail }) => {
    const ratingKey = String(detail.rating);
    setRatingsBreakdown({
      ...ratingsBreakdown,
      [ratingKey]: (ratingsBreakdown[ratingKey] || 0) + 1
    });
  };

  const onReviewDelete = review => {
    const ratingKey = String(review.rating);
    setRatingsBreakdown({
      ...ratingsBreakdown,
      [ratingKey]: (ratingsBreakdown[ratingKey] || 0) - 1
    });
  };

  const openReviewFormModal = formProps => {
    setReviewFormProps(formProps);
    toggleReviewForm(true);
  };

  const onReviewPhotoClick = (review: Review, photo: ReviewPhoto) =>
    logTrailDetailsReviewsPhotoClicked({ photo_id: photo.id.toString(), review_id: review.id, trail_id: id });

  const onReviewPhotosScroll = (review: Review) => {
    if (!carouselSwipedRef.current.has(review.id)) {
      carouselSwipedRef.current.add(review.id);
      logPhotoCarouselSwiped({
        carousel_item_id: review.id.toString(),
        page_type: 'trail page',
        photo_carousel_type: 'reviews'
      });
    }
  };

  const onReviewPhotosScrollEnd = (review: Review, numPhotos: number) =>
    logTrailDetailsReviewsPhotoEndReached({ num_photos: numPhotos, review_id: review.id, trail_id: id });

  const onSearchFocus = () => logReviewsSearchBoxClicked({ trail_id: id });

  const onSignupSuccess = useCallback((loggedInUser: User) => {
    toggleSignupModal(false);
    if (openReviewFormAfterAuth.current && !!reviewFromDeeplink.current && loggedInUser?.id === reviewFromDeeplink.current?.user.id) {
      setReviewFormProps(reviewFromDeeplink.current);
      toggleReviewForm(true);
      setTryEditFromDeeplink(false);
    }
  }, []);

  useEffect(() => {
    const fetchReviewFromDeeplink = async () => {
      if (tryEditFromDeeplink) {
        setTryEditFromDeeplink(false);
        const reviewsFromDeeplink = await get<{ trail_reviews: Review[] }>(`/api/alltrails/trails/${id}/reviews?id=${initialReviewIdFromDeeplink}`);
        const review = reviewsFromDeeplink.trail_reviews.find(review => review.id === initialReviewIdFromDeeplink);
        reviewFromDeeplink.current = review;
        if (review && user?.id === review.user.id) {
          setReviewFormProps(review);
          setTryEditFromDeeplink(false);
          if (!openReviewFormAfterAuth.current) {
            toggleReviewForm(true);
          }
        }
      }
    };

    fetchReviewFromDeeplink();
  }, [id, initialReviewIdFromDeeplink, tryEditFromDeeplink, user]);

  const hasReviews = !!totalReviewsCount;
  return (
    <div className={styles.container} ref={containerRef} tabIndex={-1} role="region" aria-labelledby={reviewsHeadingId}>
      <div className={styles.overviewAndCta}>
        <div className={styles.overview}>
          <h2 id={reviewsHeadingId} className={styles.heading}>
            {intl.formatMessage({ defaultMessage: 'Reviews' })}
          </h2>
          {hasReviews && (
            <div className={styles.summary}>
              <div className={styles.avgRating}>
                <AverageRating averageRating={avgRating} />
                <div className={styles.ugcCtas}>
                  <Link
                    className={styles.ugcEntrypoint}
                    size="sm"
                    onClick={() => {
                      logTrailDetailsReviewTabClicked({ trail_id: id });
                      setDisplayActivities(false);
                      isMediumDown
                        ? ugcRef.current?.scrollIntoView({ behavior: 'smooth' })
                        : containerRef.current?.scrollIntoView({ behavior: 'smooth' });
                    }}
                    variant="secondary"
                  >
                    {intl.formatMessage(
                      { defaultMessage: '{reviewCount, plural, one {# review} other {# reviews}}' },
                      { reviewCount: totalReviewsCount }
                    )}
                  </Link>
                  {!!trackCount && (
                    <Link className={styles.ugcEntrypoint} size="sm" onClick={onActivityEntryClicked} variant="secondary">
                      {intl.formatMessage(
                        { defaultMessage: '{activityCount, plural, one {# activity} other {# activities}}' },
                        { activityCount: trackCount }
                      )}
                    </Link>
                  )}
                </div>
              </div>
              <div className={styles.ratingsBreakdown}>
                <ReviewsGraph ratingsBreakdown={ratingsBreakdown} />
              </div>
            </div>
          )}
        </div>
        {hasReviews && (
          <>
            <Button className={styles.addReview} text={<FormattedMessage defaultMessage="Review trail" />} variant="primary" onClick={onAddReview} />
            <UgcActions slug={slug} trailId={id} />
          </>
        )}
        {isMobile && reviewSummaryCardForMobile}
      </div>
      {(!isMediumDown || (isMediumDown && hasReviews)) && <div className={classNames(layoutStyles.col1, styles.desktopSpacer)} ref={ugcRef} />}
      <div className={styles.ugcContainer}>
        {!hasReviews && (
          <>
            <ReviewsNullState
              mapId={trailDetails.verifiedMapId}
              defaultPhoto={trailDetails.defaultPhoto}
              trailInfo={trailInfo}
              handleAddReview={onAddReview}
            />
            <UgcActions className={styles.nullStateActions} slug={slug} trailId={id} />
          </>
        )}
        {(displayActivities || (!hasReviews && !!trackCount)) && <ActivitiesList trailId={id} totalActivities={trackCount} />}
        {!displayActivities && initialReviews && (
          <SearchFilterReviews
            initialReviews={initialReviews}
            onActivityLinkClick={onActivityLinkClick}
            onFilterClick={onFilterClick}
            onFilterShowMoreClick={onFilterShowMoreClick}
            onLightboxClose={onPhotoViewComplete}
            onLightboxPhotoViewed={onLightboxPhotoViewed}
            onLightboxUserClick={onLightboxUserClick}
            onLoadMore={onLoadMore}
            onNullStateCtaClick={onNullStateCtaClick}
            onNullStateViewed={onNullStateViewed}
            openReviewFormModal={openReviewFormModal}
            onDeleteReview={onReviewDelete}
            onSearchFocus={onSearchFocus}
            onResultsReturned={onResultsReturned}
            onReviewPhotoClick={onReviewPhotoClick}
            onReviewPhotosScroll={onReviewPhotosScroll}
            onReviewPhotosScrollEnd={onReviewPhotosScrollEnd}
            perPage={perPage}
            reportLocation="trail details"
            searchBoxClassName={styles.searchBar}
            total={totalReviewsCount}
            trailId={id}
            isBot={isBot}
            helpfulReviews={helpfulReviews}
            seoTestLimitReviewsToFive={seoTestLimitReviewsToFive}
            seoTestLimitReviewsToTen={seoTestLimitReviewsToTen}
            seoTestLimitReviewsToThreeLonger={seoTestLimitReviewsToThreeLonger}
            seoTestDisplay5KeywordReviews={seoTestDisplay5KeywordReviews}
            seoKeywordReviews={seoKeywordReviews}
            showVoteButton
          />
        )}
      </div>
      {user && isReviewFormOpen && (
        <ReviewFormModal
          activity={reviewFormProps?.activity}
          associatedRecording={reviewFormProps?.associatedRecording}
          associatedTrailReviewId={reviewFormProps?.associatedTrailReviewId}
          closeReviewModal={() => toggleReviewForm(false)}
          comment={reviewFormProps?.comment}
          comment_source={reviewFormProps?.comment_source}
          date={reviewFormProps?.date}
          difficulty={reviewFormProps?.difficulty}
          difficultyChipUids={reviewFormProps?.difficultyChipUids}
          id={reviewFormProps?.id}
          infoAttributes={reviewFormProps?.infoAttributes}
          isActivity={reviewFormProps?.isActivity}
          obstacles={reviewFormProps?.obstacles}
          onReviewAdd={onReviewAdd}
          rating={reviewFormProps?.rating}
          ratingAttributes={reviewFormProps?.ratingAttributes}
          reviewSource={reviewFormProps?.reviewSource}
          trailId={id}
          user={user}
        />
      )}
      {isSignupModalOpen && (
        <SignUpModal
          closeModal={() => toggleSignupModal(false)}
          deeplinkData={{ pageId: id, pageType: 'trail' }}
          isOpen={isSignupModalOpen}
          onSuccess={onSignupSuccess}
          trigger={signUpTrigger!}
          upsellTrigger="See More Reviews"
        />
      )}
    </div>
  );
};

export default Reviews;
